/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]',
			menuSublinksTertiary: '[data-menu-sublinks-tertiary]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-dc-is-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(menu) {
		console.log('inside setupFocusTrap')
		const focusableElements = menu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		this.focusTrap.firstFocusable?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		console.log('inside removeFocusTrap')
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
		this.updateMobileMenuOffset();
	},

	/**
	 * Measure actual header bottom (includes announcement bar if visible) and set CSS var
	 */
	updateMobileMenuOffset() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) return;
		const offset = Math.max(0, header.getBoundingClientRect().bottom);
		document.documentElement.style.setProperty('--mm-header-offset', `${offset}px`);
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtns = document.querySelectorAll('[data-header-burger]');
		if (burgerBtns) {
			burgerBtns.forEach(function(burgerBtn) {
				burgerBtn.addEventListener('click', () => {
					window.mmMobileMenu();
					// Update aria-expanded
					const isOpen = burgerBtn.getAttribute('aria-expanded') === 'true';
					burgerBtn.setAttribute('aria-expanded', !isOpen);
				});
			})
		}

		// Search toggle button
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				window.mmShowSearch(searchBtn);
				// Update aria-expanded
				const isOpen = searchBtn.getAttribute('aria-expanded') === 'true';
				searchBtn.setAttribute('aria-expanded', !isOpen);
			});
		}

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					window.getElement(this.config.selectors.headerBurger)?.focus();
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		// Position mega menus (data-megamenulink)
		window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
			const megaMenu = menuElem.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.top = `${headerHeight - 1}px`;
			}
		});

		// Position submenus (data-menulink with nested data-megamenu)
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle, Arrow Down to focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.toggleMegaMenu(link, megaMenu);
					this.setupFocusTrap(megaMenu);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					// Focus first link in mega menu
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			megaMenu.querySelectorAll('a').forEach((megamenuLink) => {
				const parent = megaMenu.previousElementSibling;

				megamenuLink.addEventListener('keydown', (e) => {
					if (e.key === 'Escape') {
						this.removeFocusTrap();
						parent.focus();
					}
				});
			});

			// Hover to open (keep existing behavior)
			link.addEventListener('mouseenter', () => {
				// Close other mega menus (not this one)
				const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
				allMegaMenuLinks.forEach((otherLink) => {
					if (otherLink !== link) {
						otherLink.setAttribute('aria-expanded', 'false');
						const otherMegaMenu = otherLink.nextElementSibling;
						if (otherMegaMenu?.hasAttribute('data-megamenu')) {
							otherMegaMenu.classList.remove('mm-open');
						}
					}
				});
				// Open this mega menu
				this.openMegaMenu(link, megaMenu);
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const submenu = link.querySelector('[data-megamenu]');

				if (submenu) {
					// This link HAS a submenu - close other mega menus on hover
					link.addEventListener('mouseenter', () => {
						// Close all mega menus (but CSS will handle submenu display)
						const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
						allMegaMenuLinks.forEach((megaLink) => {
							megaLink.setAttribute('aria-expanded', 'false');
							const megaMenu = megaLink.nextElementSibling;
							if (megaMenu?.hasAttribute('data-megamenu')) {
								megaMenu.classList.remove('mm-open');
							}
						});
						// Show background filter for submenu too
						this.showBackgroundFilter();
					});

					// Hide background filter when leaving submenu
					link.addEventListener('mouseleave', () => {
						this.hideBackgroundFilter();
					});
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const hasOpenMenu = Array.from(megaMenuLinks).some(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (hasOpenMenu) {
					this.closeAllMegaMenus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		// Initialize tertiary menus and product lists
		this.initTertiaryMenus();
		this.initProductLists();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');

	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Initialize tertiary menu links (show tertiary container on hover)
	 */
	initTertiaryMenus() {
		const secondaryLinkContainers = window.getElements('.mm-mega-menu-secondary-link-container');

		secondaryLinkContainers.forEach((container) => {
			const link = container.querySelector('[data-menu-sublinks]');
			if (!link) return;

			const hasTertiary = link.getAttribute('data-has-tertiary') === 'true';
			if (!hasTertiary) return;

			const tertiaryContainerSelector = link.getAttribute('data-tertiary-container');
			if (!tertiaryContainerSelector) return;

			const tertiaryContainer = window.getElement(tertiaryContainerSelector, false);
			if (!tertiaryContainer) return;

			// Show tertiary menu on hover
			container.addEventListener('mouseenter', () => {
				// Close other tertiary menus in the same mega menu
				const megaMenuId = link.getAttribute('data-mega-menu-id');
				if (megaMenuId) {
					const megaMenu = window.getElement(`#${megaMenuId}`, false);
					if (megaMenu) {
						const allTertiaryMenus = megaMenu.querySelectorAll('.mm-mega-menu-tertiary-links-container');
						allTertiaryMenus.forEach((otherTertiaryMenu) => {
							if (otherTertiaryMenu !== tertiaryContainer) {
								this.hideTertiaryMenu(otherTertiaryMenu);
							}
						});
					}
				}
				this.showTertiaryMenu(tertiaryContainer);
			});

			// Keep tertiary menu open when hovering over it
			tertiaryContainer.addEventListener('mouseenter', () => {
				this.showTertiaryMenu(tertiaryContainer);
			});

			// Hide tertiary menu when leaving container or tertiary menu
			container.addEventListener('mouseleave', (e) => {
				// Check if we're moving to the tertiary container
				const relatedTarget = e.relatedTarget;
				if (relatedTarget && tertiaryContainer.contains(relatedTarget)) {
					return; // Don't hide if moving to tertiary menu
				}
				this.hideTertiaryMenu(tertiaryContainer);
			});

			tertiaryContainer.addEventListener('mouseleave', (e) => {
				// Check if we're moving back to the secondary container
				const relatedTarget = e.relatedTarget;
				if (relatedTarget && container.contains(relatedTarget)) {
					return; // Don't hide if moving back to secondary container
				}
				this.hideTertiaryMenu(tertiaryContainer);
			});
		});

		window.log('info', 'NavigationManager', 'Tertiary menus initialized');
	},

	/**
	 * Initialize product lists (show product list on hover of tertiary link)
	 */
	initProductLists() {
		const tertiaryLinkContainers = window.getElements('.mm-mega-menu-tertiary-link-container');

		tertiaryLinkContainers.forEach((container) => {
			const link = container.querySelector('[data-tertiary-link]');
			if (!link) return;

			const targetId = link.getAttribute('data-target-id');
			if (!targetId) return;

			const productList = window.getElement(`#${targetId}`, false);
			if (!productList) return; // No products for this link

			// Show product list on hover
			container.addEventListener('mouseenter', () => {
				// Close other product lists in the same tertiary menu
				const tertiaryMenu = container.closest('.mm-mega-menu-tertiary-links-container');
				if (tertiaryMenu) {
					const allProductLists = tertiaryMenu.querySelectorAll('.mm-mega-menu-products-list');
					allProductLists.forEach((otherProductList) => {
						if (otherProductList !== productList) {
							this.hideProductList(otherProductList);
						}
					});
				}
				this.showProductList(productList);
			});

			// Keep product list open when hovering over it
			productList.addEventListener('mouseenter', () => {
				this.showProductList(productList);
			});

			// Hide product list when leaving container or product list
			container.addEventListener('mouseleave', (e) => {
				// Check if we're moving to the product list
				const relatedTarget = e.relatedTarget;
				if (relatedTarget && productList.contains(relatedTarget)) {
					return; // Don't hide if moving to product list
				}
				this.hideProductList(productList);
			});

			productList.addEventListener('mouseleave', (e) => {
				// Check if we're moving back to the tertiary container
				const relatedTarget = e.relatedTarget;
				if (relatedTarget && container.contains(relatedTarget)) {
					return; // Don't hide if moving back to tertiary container
				}
				this.hideProductList(productList);
			});
		});

		window.log('info', 'NavigationManager', 'Product lists initialized');
	},

	/**
	 * Show tertiary menu container
	 */
	showTertiaryMenu(tertiaryContainer) {
		if (!tertiaryContainer) return;
		tertiaryContainer.style.display = 'flex';
		tertiaryContainer.setAttribute('aria-hidden', 'false');
	},

	/**
	 * Hide tertiary menu container
	 */
	hideTertiaryMenu(tertiaryContainer) {
		if (!tertiaryContainer) return;
		tertiaryContainer.style.display = 'none';
		tertiaryContainer.setAttribute('aria-hidden', 'true');
		// Also hide any product lists within this tertiary menu
		const productLists = tertiaryContainer.querySelectorAll('.mm-mega-menu-products-list');
		productLists.forEach((list) => this.hideProductList(list));
	},

	/**
	 * Show product list
	 */
	showProductList(productList) {
		if (!productList) return;
		productList.style.display = 'flex';
		productList.style.pointerEvents = 'auto';
		productList.setAttribute('aria-hidden', 'false');
	},

	/**
	 * Hide product list
	 */
	hideProductList(productList) {
		if (!productList) return;
		productList.style.display = 'none';
		productList.style.pointerEvents = 'none';
		productList.setAttribute('aria-hidden', 'true');
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		megaMenu.classList.add('mm-open');
		this.showBackgroundFilter();
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		megaMenu.classList.remove('mm-open');
		// Close all tertiary menus within this mega menu
		const tertiaryMenus = megaMenu.querySelectorAll('.mm-mega-menu-tertiary-links-container');
		tertiaryMenus.forEach((tertiaryMenu) => {
			this.hideTertiaryMenu(tertiaryMenu);
		});
		// Close all product lists within this mega menu
		const productLists = megaMenu.querySelectorAll('.mm-mega-menu-products-list');
		productLists.forEach((productList) => {
			this.hideProductList(productList);
		});
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.classList.remove('mm-open');
				// Close all tertiary menus within this mega menu
				const tertiaryMenus = megaMenu.querySelectorAll('.mm-mega-menu-tertiary-links-container');
				tertiaryMenus.forEach((tertiaryMenu) => {
					this.hideTertiaryMenu(tertiaryMenu);
				});
				// Close all product lists within this mega menu
				const productLists = megaMenu.querySelectorAll('.mm-mega-menu-products-list');
				productLists.forEach((productList) => {
					this.hideProductList(productList);
				});
			}
		});
		this.hideBackgroundFilter();
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow, false);
			if (elemToShow) {
				elemToShow.style.display = 'flex';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const isOverlayOpen = () => {
			const mobileMenuOpen = window.getElement(this.config.selectors.menuMobile)?.classList.contains(this.config.classes.open);
			const searchOpen = window.getElement(this.config.selectors.searchContainer)?.classList.contains(this.config.classes.open);
			return mobileMenuOpen || searchOpen;
		};

		const toggleFilter = (show) => {
			if (!show && isOverlayOpen()) return;
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, {
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation'
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(false))
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart?.classList.contains(this.config.classes.drawerOpen);
			const mobileMenuOpen = window.getElement(this.config.selectors.menuMobile)?.classList.contains(this.config.classes.open);

			if (!searchOpen && !cartOpen && !mobileMenuOpen) {
				toggleFilter(false);
			}
		});

		filter.addEventListener('click', () => {
			MobileMenuManager.close();
			window.getElements(this.config.selectors.megaMenuLink).forEach((link) => {
				const megaMenu = link.nextElementSibling;
				if (megaMenu?.hasAttribute('data-megamenu')) {
					NavigationManager.closeMegaMenu(link, megaMenu);
				}
			});
			NavigationManager.hideBackgroundFilter();
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', {
			trigger: 'show',
			zIndex,
			hasZIndex: !!zIndex
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const mobileMenuOpen = window.getElement(this.config.selectors.menuMobile)?.classList.contains(this.config.classes.open);
		const searchOpen = window.getElement(this.config.selectors.searchContainer)?.classList.contains(this.config.classes.open);
		if (mobileMenuOpen || searchOpen) return;

		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;

		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', {
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				console.log("btn clicked");
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			});
		});

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
		this.updateMobileMenuOffset();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		const focusableElements = mobileMenu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;
		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (window.innerWidth > 1280) return;
		if (this._isToggling) return;
		this._isToggling = true;
		setTimeout(() => { this._isToggling = false; }, 300);

		const header = window.getElement('header');
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const headerMenu = window.getElement(this.config.selectors.header);
		const html = document.documentElement;
		if (!header || !mobileMenu || !headerMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = headerMenu.offsetHeight;

		[header, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));
		// Setup focus trap and background filter when menu opens
		if (mobileMenu.classList.contains(this.config.classes.open)) {
			html.classList.add(this.config.classes.stopScrolling);
			NavigationManager.showBackgroundFilter();
			mobileMenu.setAttribute('aria-hidden', 'false');
			this.setupFocusTrap(mobileMenu);
			this.focusTrap.firstFocusable?.focus({ preventScroll: true });
		} else {
			html.classList.remove(this.config.classes.stopScrolling);
			NavigationManager.hideBackgroundFilter();
			mobileMenu.setAttribute('aria-hidden', 'true');
			this.removeFocusTrap();
		}

		window.emit('global:mobile-menu-toggled', { headerHeight });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (window.innerWidth > 1280) return;

		const elements = {
			header: window.getElement('header'),
			icon: window.getElement(this.config.selectors.headerBurger),
			mobileMenu: window.getElement(this.config.selectors.menuMobile),
			html: document.documentElement
		};

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		const { header, icon, mobileMenu, html } = elements;

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		html.classList.remove(this.config.classes.stopScrolling);
		mobileMenu.setAttribute('aria-hidden', 'true');
		document.querySelectorAll(this.config.selectors.headerBurger).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

		// Remove focus trap when menu closes
		this.removeFocusTrap();

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass, false);
		const headerMenu = window.getElement(this.config.selectors.header);
		const headerHeight = headerMenu.offsetHeight;
		const headerMenuTop = headerMenu.getBoundingClientRect().top;
		const topPosition = `0px`;
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			element.style.top = topPosition;
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass, false);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', () => {
	NavigationManager.onResize();
});

let _scrollRafPending = false;
window.addEventListener('scroll', () => {
	if (_scrollRafPending) return;
	_scrollRafPending = true;
	requestAnimationFrame(() => {
		NavigationManager.updateMobileMenuOffset();
		_scrollRafPending = false;
	});
}, { passive: true });